import type { EstateJsonData } from '@types';

const VICMAP_WFS_URL = 'https://opendata.maps.vic.gov.au/geoserver/wfs';
const VIC_APPROVED_LAYER = 'open-data-platform:v_s_parcel_approved';
const VIC_PROPOSED_LAYER = 'open-data-platform:v_s_parcel_proposed';
const PAGE_SIZE = 2000;

interface VicmapParcelAttributes {
  parcel_pfi?: string;
  parcel_plan_number?: string;
  parcel_lot_number?: string;
  Shape__Area?: number;
}

interface VicmapParcelFeature {
  attributes: VicmapParcelAttributes;
  geometry: {
    rings?: number[][][];
  } | null;
}

type GeoJsonPosition = [number, number] | [number, number, number];

interface VicmapWfsFeature {
  id?: string | number;
  properties?: {
    parcel_pfi?: string;
    plan_number?: string;
    lot_number?: string;
  };
  geometry?: {
    type: 'Polygon' | 'MultiPolygon';
    coordinates: GeoJsonPosition[][] | GeoJsonPosition[][][];
  } | null;
}

interface VicmapWfsResponse {
  numberReturned?: number;
  numberMatched?: number;
  features?: VicmapWfsFeature[];
}

interface VicmapWfsException {
  exceptions?: Array<{
    exception?: Array<{
      exceptionText?: string[];
    }>;
  }>;
}

interface ProjectedPoint {
  x: number;
  y: number;
}

/**
 * Load all registered Victorian parcel polygons for a plan directly from
 * Vicmap. This replaces the former server-side ZIP/GDAL extraction worker.
 */
export async function processVicEstate(
  planNumber: string,
  address?: string,
  onProgress?: (message: string) => void
): Promise<EstateJsonData> {
  const normalizedPlan = normalizeVicPlanNumber(planNumber);
  const planPrefix = getPlanPrefix(normalizedPlan);

  onProgress?.(`Querying Vicmap for ${normalizedPlan}...`);
  const features = await fetchPlanParcels(planPrefix);
  if (features.length === 0) {
    throw new Error(
      `No registered Vicmap parcels were found for ${normalizedPlan}. ` +
        'The plan may still be proposed, or the plan number may need checking.'
    );
  }

  onProgress?.(`Found ${features.length} parcel(s). Building estate data...`);

  const usedCodes = new Set<string>();
  const centroids: { lat: number; lng: number }[] = [];
  let generatedCode = 0;

  const lots = features
    .map((feature) => {
      const ring = getLargestRing(feature.geometry?.rings);
      if (!ring) return null;

      const points = ringToPoints(ring);
      if (points.length < 4) return null;

      const rawCode = feature.attributes.parcel_lot_number?.trim() || String(generatedCode++);
      const code = makeUniqueCode(rawCode, usedCodes);
      const area =
        positiveNumber(feature.attributes.Shape__Area) ?? estimatePolygonAreaSquareMetres(points);

      centroids.push(computeCentroid(points));

      return {
        code,
        handleid: code.padStart(3, '0'),
        sourcePlanNumber: feature.attributes.parcel_plan_number?.trim() || normalizedPlan,
        sourceId: feature.attributes.parcel_pfi?.trim(),
        landsize: roundTo(area, 2),
        frontage: roundTo(computeFrontage(points), 2),
        price: 0,
        status: 'available',
        points,
      };
    })
    .filter((lot): lot is NonNullable<typeof lot> => lot !== null);

  if (lots.length === 0) {
    throw new Error(
      `Vicmap returned parcels for ${normalizedPlan}, but none contained usable polygons.`
    );
  }

  const center = averagePoint(centroids);

  return {
    geoLocation: center,
    name: normalizedPlan,
    stage: normalizedPlan,
    address: address ?? 'Victoria',
    website: '',
    status: 'draft',
    lots,
    references: [],
  };
}

export function normalizeVicPlanNumber(planNumber: string): string {
  const normalized = planNumber.trim().toUpperCase().replace(/\s+/g, '');
  if (!/^[A-Z]{1,5}\d+[A-Z0-9]*$/.test(normalized)) {
    throw new Error(
      'Enter a valid Victorian plan number such as PS604496A, LP123456, or TP123456.'
    );
  }
  return normalized;
}

async function fetchPlanParcels(planPrefix: string): Promise<VicmapParcelFeature[]> {
  const [approvedResult, proposedResult] = await Promise.allSettled([
    fetchPlanLayer(VIC_APPROVED_LAYER, planPrefix),
    fetchPlanLayer(VIC_PROPOSED_LAYER, planPrefix),
  ]);
  const approved = approvedResult.status === 'fulfilled' ? approvedResult.value : [];
  const proposed = proposedResult.status === 'fulfilled' ? proposedResult.value : [];

  // A plan can briefly exist in both feeds while Vicmap promotes it. Prefer the
  // registered/approved geometry, otherwise use the proposed parcels.
  if (approved.length > 0) return approved;
  if (proposed.length > 0) return proposed;

  const failure =
    approvedResult.status === 'rejected'
      ? approvedResult.reason
      : proposedResult.status === 'rejected'
        ? proposedResult.reason
        : null;
  if (failure instanceof Error) throw failure;
  if (failure) throw new Error(String(failure));
  return [];
}

async function fetchPlanLayer(layer: string, planPrefix: string): Promise<VicmapParcelFeature[]> {
  const features: VicmapParcelFeature[] = [];
  let startIndex = 0;
  let hasMorePages = true;

  while (hasMorePages) {
    const params = new URLSearchParams({
      service: 'WFS',
      version: '2.0.0',
      request: 'GetFeature',
      typeNames: layer,
      outputFormat: 'application/json',
      srsName: 'EPSG:4326',
      count: String(PAGE_SIZE),
      startIndex: String(startIndex),
      // These WFS layers are database views without a primary key. GeoServer
      // requires an explicit sort whenever startIndex is supplied.
      sortBy: 'parcel_pfi',
      cql_filter: `plan_number LIKE '${planPrefix}%'`,
    });

    const response = await fetch(`${VICMAP_WFS_URL}?${params.toString()}`);
    const responseText = await response.text();
    if (!response.ok) {
      throw new Error(
        `Vicmap parcel WFS query failed (${response.status}): ${responseText.substring(0, 180)}`
      );
    }

    let body: VicmapWfsResponse & VicmapWfsException;
    try {
      body = JSON.parse(responseText) as VicmapWfsResponse & VicmapWfsException;
    } catch {
      throw new Error(
        `Vicmap parcel WFS returned an invalid response: ${responseText.substring(0, 180)}`
      );
    }

    const exceptionText = body.exceptions
      ?.flatMap((group) => group.exception ?? [])
      .flatMap((exception) => exception.exceptionText ?? [])
      .filter(Boolean)
      .join(' ');
    if (exceptionText) {
      throw new Error(`Vicmap parcel WFS query failed: ${exceptionText}`);
    }

    const page = (body.features ?? []).map(wfsFeatureToParcel).filter(isParcelFeature);
    features.push(...page);
    hasMorePages = (body.features?.length ?? 0) === PAGE_SIZE;
    startIndex += body.features?.length ?? 0;
  }

  return features;
}

function wfsFeatureToParcel(feature: VicmapWfsFeature): VicmapParcelFeature | null {
  const rings = geoJsonGeometryToRings(feature.geometry);
  if (rings.length === 0) return null;

  return {
    attributes: {
      parcel_pfi: feature.properties?.parcel_pfi ?? String(feature.id ?? ''),
      parcel_plan_number: feature.properties?.plan_number,
      parcel_lot_number: feature.properties?.lot_number,
    },
    geometry: { rings },
  };
}

function isParcelFeature(feature: VicmapParcelFeature | null): feature is VicmapParcelFeature {
  return feature !== null;
}

function geoJsonGeometryToRings(geometry?: VicmapWfsFeature['geometry']): number[][][] {
  if (!geometry) return [];
  if (geometry.type === 'Polygon') {
    return geometry.coordinates as GeoJsonPosition[][] as number[][][];
  }
  return (geometry.coordinates as GeoJsonPosition[][][]).flatMap(
    (polygon) => polygon as number[][][]
  );
}

function getPlanPrefix(planNumber: string): string {
  return planNumber.match(/^[A-Z]+\d+/)?.[0] ?? planNumber;
}

function getLargestRing(rings?: number[][][]): number[][] | null {
  if (!rings?.length) return null;

  return (
    rings
      .filter((ring) => ring.length >= 3)
      .sort((a, b) => Math.abs(signedRingArea(b)) - Math.abs(signedRingArea(a)))[0] ?? null
  );
}

function signedRingArea(ring: number[][]): number {
  let area = 0;
  for (let index = 0; index < ring.length; index++) {
    const current = ring[index];
    const next = ring[(index + 1) % ring.length];
    area += current[0] * next[1] - next[0] * current[1];
  }
  return area / 2;
}

function ringToPoints(ring: number[][]): { lat: number; lng: number }[] {
  const points = ring
    .filter((coordinate) => coordinate.length >= 2)
    .map(([lng, lat]) => ({ lat, lng }));

  if (points.length > 0) {
    const first = points[0];
    const last = points[points.length - 1];
    if (first.lat !== last.lat || first.lng !== last.lng) {
      points.push({ ...first });
    }
  }

  return points;
}

function makeUniqueCode(rawCode: string, usedCodes: Set<string>): string {
  let code = rawCode;
  let suffix = 2;
  while (usedCodes.has(code)) {
    code = `${rawCode}-${suffix++}`;
  }
  usedCodes.add(code);
  return code;
}

function computeCentroid(points: { lat: number; lng: number }[]): {
  lat: number;
  lng: number;
} {
  return averagePoint(removeClosingPoint(points));
}

function averagePoint(points: { lat: number; lng: number }[]): {
  lat: number;
  lng: number;
} {
  if (points.length === 0) return { lat: -37.5, lng: 145.5 };
  return {
    lat: points.reduce((sum, point) => sum + point.lat, 0) / points.length,
    lng: points.reduce((sum, point) => sum + point.lng, 0) / points.length,
  };
}

/**
 * Preserve the former API's frontage behaviour: the short side of the
 * polygon's minimum-area bounding rectangle.
 */
function computeFrontage(points: { lat: number; lng: number }[]): number {
  const projected = projectToLocalMetres(removeClosingPoint(points));
  const hull = convexHull(projected);
  if (hull.length < 2) return 0;
  if (hull.length === 2) return distance(hull[0], hull[1]);

  let minimumArea = Infinity;
  let shortSide = 0;

  for (let index = 0; index < hull.length; index++) {
    const current = hull[index];
    const next = hull[(index + 1) % hull.length];
    const angle = Math.atan2(next.y - current.y, next.x - current.x);
    const cosine = Math.cos(angle);
    const sine = Math.sin(angle);
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    for (const point of hull) {
      const x = point.x * cosine + point.y * sine;
      const y = -point.x * sine + point.y * cosine;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }

    const width = maxX - minX;
    const height = maxY - minY;
    const area = width * height;
    if (area < minimumArea) {
      minimumArea = area;
      shortSide = Math.min(width, height);
    }
  }

  return shortSide;
}

function estimatePolygonAreaSquareMetres(points: { lat: number; lng: number }[]): number {
  const projected = projectToLocalMetres(removeClosingPoint(points));
  if (projected.length < 3) return 0;

  let area = 0;
  for (let index = 0; index < projected.length; index++) {
    const current = projected[index];
    const next = projected[(index + 1) % projected.length];
    area += current.x * next.y - next.x * current.y;
  }
  return Math.abs(area / 2);
}

function projectToLocalMetres(points: { lat: number; lng: number }[]): ProjectedPoint[] {
  if (points.length === 0) return [];
  const origin = averagePoint(points);
  const metresPerDegreeLng = 111_320 * Math.cos((origin.lat * Math.PI) / 180);
  const metresPerDegreeLat = 110_574;

  return points.map((point) => ({
    x: (point.lng - origin.lng) * metresPerDegreeLng,
    y: (point.lat - origin.lat) * metresPerDegreeLat,
  }));
}

function removeClosingPoint<T extends { lat: number; lng: number }>(points: T[]): T[] {
  if (points.length < 2) return points;
  const first = points[0];
  const last = points[points.length - 1];
  return first.lat === last.lat && first.lng === last.lng ? points.slice(0, -1) : points;
}

function convexHull(points: ProjectedPoint[]): ProjectedPoint[] {
  const sorted = [...points]
    .sort((a, b) => a.x - b.x || a.y - b.y)
    .filter(
      (point, index, values) =>
        index === 0 || point.x !== values[index - 1].x || point.y !== values[index - 1].y
    );

  if (sorted.length <= 2) return sorted;

  const cross = (origin: ProjectedPoint, a: ProjectedPoint, b: ProjectedPoint) =>
    (a.x - origin.x) * (b.y - origin.y) - (a.y - origin.y) * (b.x - origin.x);

  const lower: ProjectedPoint[] = [];
  for (const point of sorted) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 0
    ) {
      lower.pop();
    }
    lower.push(point);
  }

  const upper: ProjectedPoint[] = [];
  for (const point of [...sorted].reverse()) {
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 0
    ) {
      upper.pop();
    }
    upper.push(point);
  }

  lower.pop();
  upper.pop();
  return [...lower, ...upper];
}

function distance(a: ProjectedPoint, b: ProjectedPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function positiveNumber(value?: number): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

function roundTo(value: number, decimalPlaces: number): number {
  const factor = 10 ** decimalPlaces;
  return Math.round(value * factor) / factor;
}
